import { useMemo, useEffect, useState, useCallback } from 'react';
import theme from '@wavetrak/theme';

import { useDeviceSize } from 'hooks/useDeviceSize';
import type { Camera } from 'types/camera';

import useCamsPreferenceFromLocalStorage from './useCamsPreferenceFromLocalStorage';
import useTrackCamChanges from './useTrackCamChanges';

const NEARBY = 'NEARBY';
const ANGLES = 'ANGLES';

const useCams = (
  cameras: Camera[],
  spotId: string,
  shouldUpdateLocalStorage: boolean = false,
  hasLineupCamPermissions: boolean = true,
  includeInternalCams: boolean = false,
) => {
  const device = useDeviceSize();

  const hasMultipleCams = cameras.length > 1;

  const {
    multiCamViewActive,
    setMultiCamViewActive,
    primaryCamId,
    setPrimaryCamId,
    multiCamLoaded,
  } = useCamsPreferenceFromLocalStorage(spotId, cameras, hasMultipleCams, shouldUpdateLocalStorage);

  const isEligibleCam = useCallback(
    (cam: Camera) =>
      (hasLineupCamPermissions || !cam.isLineupCam) && (includeInternalCams || !cam.isInternalCam),
    [hasLineupCamPermissions, includeInternalCams],
  );

  const numCamsDisplayed = useMemo(() => {
    if (multiCamViewActive) {
      const eligibleCount = cameras.filter(isEligibleCam).length;
      return eligibleCount > 4 ? 4 : eligibleCount;
    }

    return 1;
  }, [multiCamViewActive, cameras, isEligibleCam]);

  const defaultActiveMenu = useCallback(() => {
    if (multiCamViewActive) {
      // default to Cam Angles on multi cam view when spot has more than 4 angles
      if (cameras?.length > 4) {
        return ANGLES;
      }
      return NEARBY;
    }
    // default to Cam Angles when single cam view is enabled on spot with multiple angles
    if (!multiCamViewActive && cameras?.length > 1) {
      return ANGLES;
    }

    return NEARBY;
  }, [cameras?.length, multiCamViewActive]);

  const [activeMenu, setActiveMenu] = useState<typeof NEARBY | typeof ANGLES>(defaultActiveMenu());
  const primaryCam = useMemo(
    () => cameras.find((cam) => cam._id === primaryCamId) || cameras[0],
    [cameras, primaryCamId],
  );

  const sortedCameras = useMemo(() => {
    if (!multiCamViewActive) return [primaryCam];
    const eligible = cameras.filter(isEligibleCam);
    const primaryIsEligible = eligible.some((cam) => cam._id === primaryCam._id);
    const orderedCams = primaryIsEligible
      ? [primaryCam, ...eligible.filter((cam) => cam._id !== primaryCam._id)]
      : eligible;
    return orderedCams.slice(0, 4);
  }, [cameras, primaryCam, multiCamViewActive, isEligibleCam]);

  useEffect(() => {
    const isMobile = device.width < theme.breakpoints.values.tabletLg;
    if (isMobile && multiCamViewActive) {
      setMultiCamViewActive(false);
    }
  }, [device.width, multiCamViewActive, setMultiCamViewActive]);

  useTrackCamChanges({
    multiCamLoaded: multiCamLoaded.current,
    primaryCam,
    multiCamViewActive,
  });

  return {
    multiCamViewActive,
    setMultiCamViewActive,
    numCamsDisplayed,
    primaryCam,
    setPrimaryCamId,
    sortedCameras,
    activeMenu,
    setActiveMenu,
    multiCamLoaded: multiCamLoaded.current,
  };
};

export default useCams;
